import type { MuxPlayerProps } from "@mux/mux-player-react";
import { DialogTrigger, Flex, Icon } from "@wayflyer/flyui";
import { IconPlay16Line } from "@wayflyer/flyui-icons/16/line";
import { cx } from "class-variance-authority";
import React, { type PropsWithChildren, useState } from "react";
import { Pressable } from "react-aria-components";
import { useIntl } from "react-intl";

import { VideoDialog } from "./dialog";
import dialogStyles from "./dialog.module.css";
import styles from "./landing.module.css";
import { MuxPlayer } from "./mux-player-react";

export function DialogVideo({
  children,
  playbackId,
  title,
}: { children: React.ReactNode } & Required<Pick<MuxPlayerProps, "playbackId" | "title">>) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  if (!React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)) {
    throw new Error("DialogVideo requires a single trigger element");
  }
  const trigger = React.cloneElement(children, {
    role: "button",
    tabIndex: children.props.tabIndex ?? 0,
    "aria-haspopup": "dialog",
    "aria-expanded": isOpen,
    "aria-label": intl.formatMessage({ id: "funder: play video", defaultMessage: "Play {title} video" }, { title }),
    className: cx(children.props.className, dialogStyles.trigger),
  });

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {React.createElement(Pressable, null, trigger)}
      <VideoDialog title={title} onClose={() => setIsOpen(false)}>
        <VideoContainer>
          <MuxPlayer autoPlay style={{ position: "absolute", inset: "0px" }} playbackId={playbackId} title={title} />
        </VideoContainer>
      </VideoDialog>
    </DialogTrigger>
  );
}

export function CardImageContainer({ children }: PropsWithChildren) {
  return (
    <div className={styles.cardImageContainer}>
      <Flex align="center" padding="2" backgroundColor="var(--palette-bg-neutral-inverse)" className={styles.playIcon}>
        <Icon color="onDark">
          <IconPlay16Line style={{ flexShrink: 0 }} aria-hidden="true" />
        </Icon>
      </Flex>
      {children}
    </div>
  );
}

// prevents layout shift https://www.mux.com/blog/mux-player-lazy-loading-with-blurhash
function VideoContainer({ children }: PropsWithChildren) {
  return (
    <div className={styles.videoContainer}>
      <div className={styles.videoPlaceholder} />
      {children}
    </div>
  );
}

type DivProps = React.ComponentPropsWithoutRef<"div"> & { as?: React.ElementType };

function createContainer(name: string, containerClass: string) {
  const Container = React.forwardRef<HTMLDivElement, DivProps>(({ as: Element = "div", className, ...props }, ref) => (
    <Element {...props} ref={ref} className={cx(containerClass, className)} />
  ));
  Container.displayName = name;
  return Container;
}

export const AccordionGridItem = createContainer("AccordionGridItem", styles.accordionGridItem);
export const Card = createContainer("Card", styles.card);
export const GridLayout = createContainer("GridLayout", styles.gridLayout);
export const HalfColGridItem = createContainer("HalfColGridItem", styles.halfColGridItem);
export const FullColGridItem = createContainer("FullColGridItem", styles.gridItem);

export const CardImg = React.forwardRef<
  HTMLImageElement,
  React.ComponentPropsWithoutRef<"img"> & { as?: React.ElementType }
>(
  (
    {
      as: Element = "img",
      className,
      alt,
      height = 360,
      sizes = "(max-width: 768) 100vw, 40vw",
      decoding = "async",
      loading = "lazy",
      ...props
    },
    ref
  ) => (
    <Element
      {...props}
      alt={alt}
      ref={ref}
      className={cx(styles.cardImg, className)}
      height={height}
      sizes={sizes}
      decoding={decoding}
      loading={loading}
    />
  )
);
CardImg.displayName = "CardImg";

export const HeroList = React.forwardRef<
  HTMLUListElement,
  React.ComponentPropsWithoutRef<"ul"> & { as?: React.ElementType }
>(({ as: Element = "ul", className, ...props }, ref) => (
  <Element {...props} ref={ref} className={cx(styles.heroList, className)} />
));
HeroList.displayName = "HeroList";

export const StyledLi = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<"li"> & { as?: React.ElementType }
>(({ as: Element = "li", className, ...props }, ref) => (
  <Element {...props} ref={ref} className={cx(styles.listItem, className)} />
));
StyledLi.displayName = "StyledLi";

export function getImageSrcSet(src: string) {
  return `${src}?w=640&q=75 640w, ${src}?w=750&q=75 750w, ${src}?w=828&q=75 828w, ${src}?w=1080&q=75 1080w, ${src}?w=1200&q=75 1200w, ${src}?w=1920&q=75 1920w, ${src}?w=2048&q=75 2048w, ${src}?w=3840&q=75 3840w`;
}
